/**
 * The Skills transports for carbon accounting (D7, D197): `@bono-packages/skills` owns the endpoint
 * paths and body shapes; this host owns the base URL, the credentials and the tenant.
 *
 * Same platform pattern as extraction — the browser never calls the agent directly. saas-api relays
 * everything under `/company/{companyId}/skills` and mints the agent's identity headers server-side,
 * so the company in the path is the tenant and nothing the browser claims can change it.
 *
 * The base is a function, not a constant: the company can change under the user without a reload
 * (the company switcher writes `companyId` to localStorage), and a base captured once would keep
 * sending the previous tenant's requests.
 */
import { createAxiosHttpPort, parseSseFrames, SkillsHttpError } from "@bono-packages/skills/runtime";
import type { HttpPort, SsePort, UploadPort } from "@bono-packages/skills/runtime";
import TokenService from "@/services/tokenService";
import api from "@/services/api";

const BASE_API = import.meta.env.VITE_APP_BASE_URL as string;

/** Where saas-api mounts the Skills relay, for this company. */
export function skillsApiBase(): string {
  return `/company/${localStorage.getItem("companyId") ?? ""}/skills`;
}

export const skillsHttp: HttpPort = createAxiosHttpPort(api);

/** A file for a Skill — a source upload, a file with a chat message — under the field `file`. */
export const skillsUpload: UploadPort = async (path, file) => {
  const form = new FormData();
  form.append("file", file, file.name);
  const response = await api.post(path, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    authorization: `Bearer ${TokenService.getLocalAccessToken() ?? ""}`,
    "Accept-Language": localStorage.getItem("locale") ?? "en",
  };
  const idToken = localStorage.getItem("idToken");
  if (idToken) headers["X-ID-TOKEN"] = idToken;
  const companyId = localStorage.getItem("companyId");
  if (companyId) headers["X-COMPANY-ID"] = companyId;
  return headers;
}

/**
 * The block stream. `fetch`, not `EventSource`: a conversation turn is a POST with a body and
 * EventSource can send neither that nor an Authorization header. `null` when the backend URL is
 * missing — the instance screen then renders read-only instead of failing on every turn.
 */
export const skillsSse: SsePort | null = !BASE_API
  ? null
  : async ({ path, method, body }, onEvent, signal) => {
      const url = BASE_API.replace(/\/+$/, "") + path;
      const headers = authHeaders();
      if (body !== undefined) headers["content-type"] = "application/json";
      const response = await fetch(url, {
        method,
        signal,
        headers: { ...headers, accept: "text/event-stream" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      if (!response.ok || !response.body) {
        const detail = await response.text().catch(() => "");
        throw new SkillsHttpError(
          response.status,
          detail.slice(0, 400) || `Skills API responded ${response.status}`,
          path
        );
      }
      await parseSseFrames(response.body, onEvent);
    };

/**
 * The same stream, for `@bono-packages/studio` (D240): the explorer's and the automations screen's
 * conversations ride the Skills relay rather than a port of their own.
 *
 * Studio composes its thread paths as `${skillsBase}/surfaces/…` from a string captured when the
 * plugin is installed, and carbon's base carries the company id. That id is not in localStorage yet
 * on a first login — `LayoutDefault` writes it after `main.ts` has already run, with no reload — so
 * a captured base would stay `/company//skills` for the whole session. The company segment is
 * therefore repaired here, at call time, which covers the company switcher too.
 */
export const studioThread: SsePort | null = skillsSse === null
  ? null
  : async (request, onEvent, signal) =>
      skillsSse(
        { ...request, path: request.path.replace(/^\/company\/[^/]*\/skills/, skillsApiBase()) },
        onEvent,
        signal
      );
